import React from 'react';
import * as Sentry from '@sentry/react';
import { Icon, Container, Header, Button } from 'semantic-ui-react';

interface FallbackProps {
    error: Error;
    componentStack: string | null;
    eventId: string | null;
    resetError: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({
    error,
    componentStack,
    eventId,
    resetError
}) => (
    <Container style={{ padding: '60px 20px', textAlign: 'center' }}>
        <Icon name="warning circle" size="huge" color="red" />
        <Header as="h1">Oops! Something went wrong</Header>
        <p style={{ marginTop: '20px', color: '#666' }}>
            We&apos;re sorry for the inconvenience. The error has been reported
            to our team.
        </p>
        {eventId && (
            <p style={{ marginTop: '10px', color: '#888' }}>
                Support reference: <code>{eventId}</code>
            </p>
        )}
        {process.env.NODE_ENV !== 'production' && (
            <div
                style={{
                    marginTop: '20px',
                    padding: '20px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    textAlign: 'left',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '400px'
                }}
            >
                <strong>Error Details:</strong>
                <pre>{error.message}</pre>
                <pre>{error.stack}</pre>
                {componentStack && (
                    <React.Fragment>
                        <strong>Component Stack:</strong>
                        <pre>{componentStack}</pre>
                    </React.Fragment>
                )}
            </div>
        )}
        <div style={{ marginTop: '30px' }}>
            <Button primary onClick={resetError}>
                Try Again
            </Button>
            <Button
                onClick={() => {
                    window.location.href = '/';
                }}
            >
                Go to Home
            </Button>
        </div>
    </Container>
);

const SentryErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    return (
        <Sentry.ErrorBoundary
            fallback={(props) => <ErrorFallback {...props} />}
            showDialog
        >
            {children}
        </Sentry.ErrorBoundary>
    );
};

export default SentryErrorBoundary;
